import { toaster } from '@/components/ui/toaster';
import { GC_TIME_VERY_LONG, QueryKey, STALE_TIME_VERY_LONG } from '@/constants/shared';
import { makeRequest, sendSetUserExperienceMetadataRequest } from '@/network/api';
import { User } from '@/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccount } from 'wagmi';

export const useUser = () => {
  const { address } = useAccount();
  const queryClient = useQueryClient();
  const {
    data: user,
    isPending: isPendingUser,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.UserExperienceMeta, address],
    queryFn: async (): Promise<User | null> => {
      if (!address) return null;
      try {
        const res = await makeRequest('/user');
        const resJson = await res.json();
        return resJson.data;
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    staleTime: STALE_TIME_VERY_LONG,
    gcTime: GC_TIME_VERY_LONG,
  });

  const mutateFavorites = async (updater: (prev: string[]) => string[], actionLabel: string) => {
    const previousUser = queryClient.getQueryData<User | null>([QueryKey.UserExperienceMeta, address]);
    try {
      if (!previousUser) throw new Error('No user found');

      const prevFavorites = previousUser.userExperienceMeta?.favorites ?? [];
      const newFavorites = updater(prevFavorites);

      const optimisticUser: User = {
        ...previousUser,
        userExperienceMeta: {
          ...(previousUser.userExperienceMeta ?? {}),
          favorites: newFavorites,
        },
      };

      queryClient.setQueryData<User | null>([QueryKey.UserExperienceMeta, address], optimisticUser);
      await sendSetUserExperienceMetadataRequest({
        ...(previousUser.userExperienceMeta ?? {}),
        favorites: newFavorites,
      });
      await refetch();
    } catch (err) {
      console.error(`Error during ${actionLabel}`, err);
      toaster.create({
        title: `Error ${actionLabel}`,
        description: String(err),
        type: 'error',
        action: {
          label: '✖',
          // onClick automatically dismisses the toast
          // so we dont need this to do anything else
          onClick: () => {},
        },
      });

      // Rollback
      queryClient.setQueryData<User | null>([QueryKey.UserExperienceMeta, address], previousUser);
    }
  };

  const addFavorite = async (name: string) => {
    await mutateFavorites((prev) => [...prev, name], `favoriting ${name}`);
  };

  const removeFavorite = async (name: string) => {
    await mutateFavorites((prev) => prev.filter((n) => n !== name), `unfavoriting ${name}`);
  };

  return {
    user,
    isPendingUser,
    addFavorite,
    removeFavorite,
    favorites: user?.userExperienceMeta?.favorites ?? [],
  };
};
